import React from 'react';
import { Box, Drawer, useTheme } from '@mui/material';
import ValueSelectorPanel from './ValueSelectorPanel';
import {
  PropertySelectionPanel,
  type PropertySelectionPanelProps,
} from '../../../../shared/ui/propertySelection/PropertySelectionPanel';
import { quickFillLayout } from '../../../../theme/theme';
import PanelShell from '../../../../shared/ui/layout/PanelShell';

const VALUE_PANEL_WIDTH = quickFillLayout.sidePanelWidth;
const PROPERTY_PANEL_WIDTH = quickFillLayout.sidePanelWidth;
const INLINE_PANEL_WIDTH = `clamp(240px, 28vw, ${quickFillLayout.sidePanelWidth}px)`;
const FOOTER_CLEARANCE_PX = 40;

type ValueSelectorPanelProps = React.ComponentProps<typeof ValueSelectorPanel>;

export interface QuickFillValuePanelState {
  shouldShow: boolean;
  showInlineTourPanel: boolean;
  panelRef?: React.Ref<HTMLDivElement>;
  columnLabel: string | null;
  options: ValueSelectorPanelProps['options'];
  currentValue?: string;
  onSelect: ValueSelectorPanelProps['onSelect'];
  onClearSelection: ValueSelectorPanelProps['onClearSelection'];
  onClearColumn: ValueSelectorPanelProps['onClearColumn'];
  onClose: ValueSelectorPanelProps['onClose'];
  usageStats: NonNullable<ValueSelectorPanelProps['usageStats']>;
  selectedCount: number;
  searchInputRef?: ValueSelectorPanelProps['searchInputRef'];
}

export interface QuickFillPropertyPanelState {
  open: boolean;
  showInlineTourPanel: boolean;
  panelRef?: React.Ref<HTMLDivElement>;
  properties: PropertySelectionPanelProps['properties'];
  extraProperties: NonNullable<PropertySelectionPanelProps['extraProperties']>;
  sharedParameters: PropertySelectionPanelProps['sharedParameters'];
  availableParameters: NonNullable<PropertySelectionPanelProps['availableParameters']>;
  workflowMode: PropertySelectionPanelProps['workflowMode'];
  workflowParameters: PropertySelectionPanelProps['workflowParameters'];
  highlightedLabels: NonNullable<PropertySelectionPanelProps['highlightedLabels']>;
  searchInputRef?: PropertySelectionPanelProps['searchInputRef'];
  onPropertyCheckedChange: PropertySelectionPanelProps['onPropertyCheckedChange'];
  onParameterChange: PropertySelectionPanelProps['onParameterChange'];
  onAddExtraProperty?: PropertySelectionPanelProps['onAddExtraProperty'];
  onRemoveExtraProperty?: PropertySelectionPanelProps['onRemoveExtraProperty'];
  onExpandChange?: PropertySelectionPanelProps['onExpandChange'];
}

export interface QuickFillSidePanelsProps {
  useDrawerPanels: boolean;
  valuePanel: QuickFillValuePanelState;
  propertyPanel: QuickFillPropertyPanelState;
}

export const QuickFillSidePanels: React.FC<QuickFillSidePanelsProps> = ({
  useDrawerPanels,
  valuePanel,
  propertyPanel,
}) => {
  const theme = useTheme();

  const renderValuePanel = () => {
    if (!valuePanel.columnLabel) {
      return null;
    }

    return (
      <ValueSelectorPanel
        columnLabel={valuePanel.columnLabel}
        options={valuePanel.options}
        searchInputRef={valuePanel.searchInputRef}
        currentValue={valuePanel.currentValue}
        onSelect={valuePanel.onSelect}
        onClearSelection={valuePanel.onClearSelection}
        onClearColumn={valuePanel.onClearColumn}
        onClose={valuePanel.onClose}
        usageStats={valuePanel.usageStats}
        selectedCount={valuePanel.selectedCount}
      />
    );
  };

  const renderPropertyPanel = () => (
    <PropertySelectionPanel
      properties={propertyPanel.properties}
      extraProperties={propertyPanel.extraProperties}
      searchInputRef={propertyPanel.searchInputRef}
      sharedParameters={propertyPanel.sharedParameters}
      availableParameters={propertyPanel.availableParameters}
      workflowMode={propertyPanel.workflowMode}
      workflowParameters={propertyPanel.workflowParameters}
      highlightedLabels={propertyPanel.highlightedLabels}
      onPropertyCheckedChange={propertyPanel.onPropertyCheckedChange}
      onParameterChange={propertyPanel.onParameterChange}
      onAddExtraProperty={propertyPanel.onAddExtraProperty}
      onRemoveExtraProperty={propertyPanel.onRemoveExtraProperty}
      expanded={true}
      onExpandChange={propertyPanel.onExpandChange}
    />
  );

  const inlinePanelSx = {
    width: INLINE_PANEL_WIDTH,
    maxWidth: '34vw',
    height: '100%',
    flexShrink: 0,
    minHeight: 0,
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'stretch',
    animation: 'slideIn 0.3s ease-out',
    '@keyframes slideIn': {
      from: { opacity: 0, transform: 'translateX(8px)' },
      to: { opacity: 1, transform: 'translateX(0)' },
    },
  } as const;

  const fixedPanelSx = (width: number) => ({
    position: 'fixed',
    top: 0,
    right: 0,
    bottom: `${FOOTER_CLEARANCE_PX}px`,
    width: { xs: '100%', sm: width },
    maxWidth: '100vw',
    height: `calc(100vh - ${FOOTER_CLEARANCE_PX}px)`,
    overflow: 'hidden',
    zIndex: theme.zIndex.drawer,
  });

  const drawerPaperSx = {
    width: { xs: '100%', sm: VALUE_PANEL_WIDTH },
    maxWidth: '100vw',
    top: 0,
    bottom: `${FOOTER_CLEARANCE_PX}px`,
    height: `calc(100vh - ${FOOTER_CLEARANCE_PX}px)`,
  };

  return (
    <>
      {valuePanel.shouldShow && !useDrawerPanels ? (
        <PanelShell
          ref={valuePanel.panelRef}
          data-testid="quickfill-tour-target-value-panel"
          borderPosition="left"
          sx={inlinePanelSx}
        >
          {renderValuePanel()}
        </PanelShell>
      ) : null}

      {propertyPanel.open && !useDrawerPanels ? (
        <PanelShell
          ref={propertyPanel.panelRef}
          data-testid="quickfill-tour-target-column-panel"
          borderPosition="left"
          sx={inlinePanelSx}
        >
          {renderPropertyPanel()}
        </PanelShell>
      ) : null}

      {valuePanel.showInlineTourPanel ? (
        <Box sx={fixedPanelSx(VALUE_PANEL_WIDTH)}>
          <PanelShell
            ref={valuePanel.panelRef}
            data-testid="quickfill-tour-target-value-panel"
            borderPosition="left"
            sx={{ width: '100%', height: '100%' }}
          >
            {renderValuePanel()}
          </PanelShell>
        </Box>
      ) : (
        <Drawer
          anchor="right"
          open={Boolean(useDrawerPanels && valuePanel.shouldShow)}
          onClose={valuePanel.onClose}
          aria-label="Quick Fill value selector"
          PaperProps={{
            'aria-label': 'Quick Fill value selector',
            sx: drawerPaperSx,
          }}
        >
          {valuePanel.columnLabel ? (
            <PanelShell
              ref={valuePanel.panelRef}
              data-testid="quickfill-tour-target-value-panel"
              borderPosition="left"
              sx={{ width: '100%', height: '100%', overflow: 'hidden' }}
            >
              {renderValuePanel()}
            </PanelShell>
          ) : null}
        </Drawer>
      )}

      {propertyPanel.showInlineTourPanel ? (
        <Box sx={fixedPanelSx(PROPERTY_PANEL_WIDTH)}>
          <PanelShell
            ref={propertyPanel.panelRef}
            data-testid="quickfill-tour-target-column-panel"
            borderPosition="left"
            sx={{ width: '100%', height: '100%' }}
          >
            {renderPropertyPanel()}
          </PanelShell>
        </Box>
      ) : (
        <Drawer
          anchor="right"
          open={useDrawerPanels && propertyPanel.open}
          onClose={() => propertyPanel.onExpandChange?.(false)}
          aria-label="Quick Fill column manager"
          PaperProps={{
            'aria-label': 'Quick Fill column manager',
            sx: drawerPaperSx,
          }}
        >
          <PanelShell
            ref={propertyPanel.panelRef}
            data-testid="quickfill-tour-target-column-panel"
            borderPosition="left"
            sx={{ width: '100%', height: '100%', overflow: 'hidden' }}
          >
            {renderPropertyPanel()}
          </PanelShell>
        </Drawer>
      )}
    </>
  );
};

export default QuickFillSidePanels;
